import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { Reveal } from "../anim";
import { Rocket, ArrowUpRight } from "lucide-react";
import { ProductStack, PRODUCTS, useCampaignProducts, priceText } from "../ProductStack";

const CATS = ["Gifts", "Wearable Art", "Home Decor", "Furniture", "Packaging"];

export const CampaignMarquee = () => {
  const [index, setIndex] = useState(0);
  const products = useCampaignProducts();

  useEffect(() => {
    const t = setInterval(() => setIndex((p) => (p + 1) % products.length), 2600);
    return () => clearInterval(t);
  }, [products.length]);

  const p = products[index] || PRODUCTS[0];
  const price = priceText(p);

  return (
    <section id="campaign" data-testid="campaign-section" className="relative">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-20 md:py-32">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <span className="text-sm font-bold uppercase tracking-[0.25em] text-[#A7D3E8]">
              The Campaign
            </span>
            <h2 className="mt-6 font-display font-black uppercase tracking-tighter leading-[0.88] text-[10vw] lg:text-[6.6vw] text-[#EBE6E0]">
              Crowdfund ideas.<br />
              Let's create<br />
              <span className="text-[#D7E85C]">what's next.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-4">
            <div data-testid="campaign-stats" className="border border-white/10 rounded-2xl p-6 bg-[#16294B]">
              <div className="flex items-center gap-4">
                <ProductStack index={index} className="h-16 w-16" products={products} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#EBE6E0] truncate" data-testid="campaign-product-name">{p.name}</div>
                  {price ? (
                    <div className="flex items-baseline gap-2 w-full">
                      <span className="font-display font-black text-4xl text-[#FF8473]" data-testid="campaign-price">
                        {price}
                      </span>
                      {p.backers !== undefined && (
                        <span className="ml-auto font-bold text-[#D7E85C] uppercase whitespace-nowrap" data-testid="campaign-backers">
                          {p.backers.toLocaleString()} BACKERS
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-display font-black text-4xl text-[#FF8473]" data-testid="campaign-raised">
                        ${p.raised.toLocaleString()}
                      </span>
                      <span className="ml-auto font-bold text-[#D7E85C] uppercase whitespace-nowrap">{p.backers.toLocaleString()} BACKERS</span>
                    </div>
                  )}
                </div>
              </div>
              {p.percent !== undefined && (
                <>
                  <div className="mt-4 h-2 w-full rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#D7E85C] transition-[width] duration-700"
                      style={{ width: `${p.percent}%` }}
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-end text-sm">
                    <span className="text-[#EBC9B5] font-bold">{p.percent}% of Target</span>
                  </div>
                </>
              )}
              <button
                data-testid="campaign-back-btn"
                onClick={() => document.getElementById("join")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#D7E85C] text-[#1F3864] py-3 rounded-full font-bold uppercase text-sm hover:bg-[#EBE6E0] active:scale-95 transition-[background,transform,color] duration-200"
              >
                Back this product <Rocket size={16} />
              </button>
            </div>
            <div className="px-6">
              <button
                data-testid="browse-all-products-btn"
                onClick={() => document.getElementById("campaign")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#EBC9B5] text-[#1F3864] py-3 rounded-full font-bold uppercase text-sm hover:bg-[#00AF95] hover:text-[#EBE6E0] active:scale-95 transition-[background,transform,color] duration-200"
              >
                Browse all products <ArrowUpRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </div>

      {/* editorial marquee */}
      <div className="bg-[#D7E85C] py-5 md:py-7 select-none overflow-hidden">
        <Marquee autoFill speed={70} gradient={false} data-testid="category-marquee">
          {CATS.map((c, i) => (
            <span key={i} className="mx-8 md:mx-12 inline-flex items-center gap-8 md:gap-12">
              <span className="font-display font-black uppercase tracking-tight text-4xl md:text-7xl text-[#1F3864]">
                {c}
              </span>
              <span className="text-[#A7D3E8] text-3xl md:text-5xl">✳</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
};
